import type { ReactElement } from 'react';
import { KeyRound, UsersRound } from 'lucide-react';

import { IntegrationCard } from './IntegrationCard';
import { renderDefaultActions } from './defaultActions';

export interface ScimIntegrationCardProps {
  /** True when the org has an active SCIM connection. */
  connected: boolean;
  /** The connected token's name, shown as card meta when connected. */
  connectionSummary?: string | null;
  onConnect?: () => void;
  onManage?: () => void;
  isLoading?: boolean;
  readOnly?: boolean;
}

/**
 * Integration tile for SCIM provisioning (KIR-4693), matching the SAML SSO /
 * SIEM cards: icon + title + status pill + Configure/Manage actions. All the
 * generate/revoke/URL detail lives in the management modal, opened from here.
 */
export function ScimIntegrationCard(
  props: ScimIntegrationCardProps
): ReactElement {
  const {
    connected,
    connectionSummary,
    onConnect,
    onManage,
    isLoading,
    readOnly,
  } = props;

  return (
    <IntegrationCard
      icon={<UsersRound size={20} aria-hidden />}
      title="SCIM Provisioning"
      subtitle="Auto-provision and revoke users from your identity provider"
      status={connected ? 'configured' : 'not_configured'}
      isLoading={isLoading}
    >
      {connected && connectionSummary && (
        <IntegrationCard.Meta>
          <span className="inline-flex items-center gap-1.5">
            <KeyRound className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {connectionSummary}
          </span>
        </IntegrationCard.Meta>
      )}
      {/* KIR-5101: SCIM is set up, not "connected" — label the CTA Configure. */}
      {renderDefaultActions(
        connected,
        { onConnect, onManage },
        readOnly,
        'Configure'
      )}
    </IntegrationCard>
  );
}

ScimIntegrationCard.displayName = 'ScimIntegrationCard';
